import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import { ArrowUp, ArrowDown, ArrowUpDown, Download, Image as ImageIcon, Settings2, Tv, Award, AlertTriangle, X, BookOpen } from 'lucide-react';
import {
  getSsgPeriods, getSsgLeaderboard, getSsgBadges, getSsgProgression, getSsgSessions, getSsgSessionPoints, closeSsgPeriod,
} from '../../api';
import { SSG_COMPETITION_NAME, ssgResultMeta } from '../../ssgConstants';
import SsgTvTokensAdmin from './SsgTvTokensAdmin';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const sectionTitle = { fontSize: 13, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };
const TOP5_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const MUTED_GREY = '#475569';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function toCsv(headers, rows) {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function RankCell({ rank, sharedRank }) {
  if (rank <= 3 && !sharedRank) {
    return <span style={{ fontSize: 18 }}>{MEDALS[rank]}</span>;
  }
  return <span style={{ fontWeight: 700 }}>{sharedRank ? '=' : ''}{rank}</span>;
}
function DeltaArrow({ delta }) {
  if (!delta) return <span style={{ color: 'var(--text-dimmed)', fontSize: 11 }}>–</span>;
  const up = delta > 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: up ? '#10b981' : '#ef4444' }}>
      {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(delta)}
    </span>
  );
}
function FormPills({ form }) {
  if (!form?.length) return <span style={{ color: 'var(--text-dimmed)', fontSize: 11 }}>—</span>;
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {form.map((r, i) => {
        const meta = ssgResultMeta({ W: 3, D: 1, L: 0 }[r]);
        return (
          <span key={i} title={meta.label} style={{
            width: 18, height: 18, borderRadius: 4, fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: meta.color + '33', color: meta.color,
          }}>{r}</span>
        );
      })}
    </div>
  );
}
const BADGE_EMOJI = {
  unbeaten: '🛡️', on_fire: '🔥', bonus_hunter: '💰', mvp: '⭐',
  climber: '📈', flawless: '💎', period_champion: '🏆',
};
function BadgeChips({ badges }) {
  if (!badges?.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 2, marginLeft: 6 }}>
      {badges.map(b => <span key={b.key} title={b.label} style={{ fontSize: 12 }}>{BADGE_EMOJI[b.key] || '•'}</span>)}
    </span>
  );
}

export default function SsgStandings({ user, onManageSessions, onOpenHallOfFame }) {
  const [periods, setPeriods] = useState([]);
  const [tabMode, setTabMode] = useState('period'); // period | season | alltime
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [standings, setStandings] = useState([]);
  const [badgesByPlayer, setBadgesByPlayer] = useState({});
  const [progression, setProgression] = useState([]);
  const [chartMode, setChartMode] = useState('rank'); // rank | points
  const [hoveredPlayer, setHoveredPlayer] = useState(null);
  const [isolatedPlayer, setIsolatedPlayer] = useState(null);
  const [pointsGrid, setPointsGrid] = useState({}); // sessionId -> { playerId -> pointsRow }
  const [sessionsForGrid, setSessionsForGrid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);
  const [bonusSort, setBonusSort] = useState({ key: 'amount', dir: 'desc' });
  const [exportingPng, setExportingPng] = useState(false);
  const [showTvTokens, setShowTvTokens] = useState(false);
  const [hasDraftSessions, setHasDraftSessions] = useState(false);
  const [maxMatchdayEver, setMaxMatchdayEver] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState('');

  const leaderboardPanelRef = useRef(null);

  useEffect(() => {
    getSsgPeriods().then(rows => {
      setPeriods(rows);
      const active = rows.find(p => p.status === 'active') || rows[0];
      setSelectedPeriodId(active?.id ?? null);
      // "A period whose end matchday has passed" has no fixture calendar to check against (see
      // Stage 1/2's notes on this) — approximated instead from real recorded activity: the
      // highest matchday any session has ever been created for, across the whole season. If
      // that's already past a period's own end_matchday, sessions have moved on and the period
      // is ready to close. A hint for staff judgement, not a hard gate.
      const season = rows[0]?.season;
      if (season) {
        getSsgSessions({ season, requesterRole: 'staff' }).then(sessions => {
          const max = (sessions || []).reduce((m, s) => Math.max(m, s.matchday), 0);
          setMaxMatchdayEver(max || null);
        });
      }
    });
  }, []);

  // ── Standings (all tab modes) ────────────────────────────────────────────────
  const refreshStandings = useCallback(() => {
    if (!selectedPeriodId) return;
    setLoading(true);
    getSsgLeaderboard(selectedPeriodId, tabMode)
      .then(rows => setStandings(rows || []))
      .finally(() => setLoading(false));
  }, [selectedPeriodId, tabMode]);
  useEffect(() => { refreshStandings(); }, [refreshStandings]);

  // ── Period-only extras: badges, progression, session grid (heatmap + bonus audit) ──────────
  useEffect(() => {
    if (tabMode !== 'period' || !selectedPeriodId) {
      setBadgesByPlayer({});
      setProgression([]);
      setPointsGrid({});
      setSessionsForGrid([]);
      return;
    }
    getSsgBadges(selectedPeriodId).then(rows => {
      const map = {};
      (rows || []).forEach(r => { map[r.player_id] = r.badges; });
      setBadgesByPlayer(map);
    });
    getSsgProgression(selectedPeriodId).then(rows => setProgression(rows || []));

    setGridLoading(true);
    getSsgSessions({ period_id: selectedPeriodId, requesterRole: 'staff' })
      .then(async sessions => {
        setHasDraftSessions((sessions || []).some(s => s.status === 'draft'));
        const published = (sessions || []).filter(s => s.status === 'published').sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
        setSessionsForGrid(published);
        const pointsPerSession = await Promise.all(published.map(s => getSsgSessionPoints(s.id)));
        const grid = {};
        published.forEach((s, i) => {
          grid[s.id] = {};
          (pointsPerSession[i] || []).forEach(r => { grid[s.id][r.player_id] = r; });
        });
        setPointsGrid(grid);
      })
      .finally(() => setGridLoading(false));
  }, [tabMode, selectedPeriodId]);

  const selectedPeriod = periods.find(p => p.id === selectedPeriodId);

  const daysRemaining = useMemo(() => {
    if (!selectedPeriod?.end_date) return null;
    const diff = Math.ceil((new Date(selectedPeriod.end_date) - new Date()) / 86400000);
    return diff >= 0 ? diff : null;
  }, [selectedPeriod]);

  // ── Bump chart data shaping ───────────────────────────────────────────────────
  const chartPlayers = useMemo(() => {
    // "Top 5" for colouring = final rank at the last session in the period.
    const ranked = [...progression].map(p => ({
      ...p, finalRank: p.series.length ? p.series[p.series.length - 1].rank : 999,
    })).sort((a, b) => a.finalRank - b.finalRank);
    return ranked.map((p, i) => ({ ...p, color: i < 5 ? TOP5_COLORS[i] : MUTED_GREY, top5: i < 5 }));
  }, [progression]);

  const chartData = useMemo(() => {
    // Recharts wants one row per session with each player's value as a separate key.
    const bySession = {};
    chartPlayers.forEach(p => {
      p.series.forEach(pt => {
        if (!bySession[pt.session_id]) bySession[pt.session_id] = { session_id: pt.session_id, date: fmtDate(pt.session_date) };
        bySession[pt.session_id][`p${p.player_id}`] = chartMode === 'rank' ? pt.rank : pt.cumulative;
      });
    });
    return Object.values(bySession).sort((a, b) => a.session_id - b.session_id);
  }, [chartPlayers, chartMode]);

  // ── Bonus audit rows (derived from the session grid) ────────────────────────────
  const bonusRows = useMemo(() => {
    const rows = [];
    sessionsForGrid.forEach(s => {
      Object.values(pointsGrid[s.id] || {}).forEach(pt => {
        if (pt.bonus_points > 0) {
          const player = standings.find(st => st.player_id === pt.player_id);
          rows.push({
            session_id: s.id, session_date: s.session_date, matchday: s.matchday,
            player_id: pt.player_id, name: player?.name || `#${pt.player_id}`,
            amount: pt.bonus_points, reason: pt.bonus_reason || '',
          });
        }
      });
    });
    const dir = bonusSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (bonusSort.key === 'amount') return (a.amount - b.amount) * dir;
      if (bonusSort.key === 'date') return (new Date(a.session_date) - new Date(b.session_date)) * dir;
      return String(a[bonusSort.key]).localeCompare(String(b[bonusSort.key])) * dir;
    });
    return rows;
  }, [sessionsForGrid, pointsGrid, standings, bonusSort]);

  const toggleBonusSort = (key) => {
    setBonusSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };
  const SortIcon = ({ active, dir }) => active ? (dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} style={{ opacity: 0.4 }} />;

  // ── Exports ────────────────────────────────────────────────────────────────────
  const exportStandingsCsv = () => {
    const headers = ['Rank', 'Player', 'P', 'W', 'D', 'L', 'Bonus', 'Pts', 'Avg/session', 'MVPs'];
    const rows = standings.map(s => [
      `${s.shared_rank ? '=' : ''}${s.rank}`, s.name, s.played, s.won, s.drawn, s.lost,
      s.bonus_points, s.total_points, s.avg_points_per_session ?? '', s.mvp_count,
    ]);
    downloadBlob(new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' }), `${SSG_COMPETITION_NAME.replace(/\s+/g, '-').toLowerCase()}-standings.csv`);
  };
  const exportBonusCsv = () => {
    const headers = ['Date', 'Matchday', 'Player', 'Amount', 'Reason'];
    const rows = bonusRows.map(r => [fmtDate(r.session_date), r.matchday, r.name, r.amount, r.reason]);
    downloadBlob(new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' }), 'bonus-audit.csv');
  };
  const exportLeaderboardPng = async () => {
    if (!leaderboardPanelRef.current) return;
    setExportingPng(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg-card').trim() || '#111827';
      const canvas = await html2canvas(leaderboardPanelRef.current, { scale: 2, useCORS: true, backgroundColor: bgColor, logging: false });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${SSG_COMPETITION_NAME.replace(/\s+/g, '-').toLowerCase()}-leaderboard.png`;
      a.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    } finally {
      setExportingPng(false);
    }
  };

  const handleClosePeriod = async () => {
    if (!selectedPeriod) return;
    setClosing(true);
    setCloseError('');
    try {
      await closeSsgPeriod(selectedPeriod.id, 'admin');
      setShowCloseConfirm(false);
      // Refresh everything so the just-closed period's status flips and the champion banner
      // + frozen standings render immediately, without a manual page reload.
      const rows = await getSsgPeriods();
      setPeriods(rows);
      refreshStandings();
    } catch (err) {
      setCloseError(err.message || 'Failed to close period.');
    } finally {
      setClosing(false);
    }
  };

  const isPeriodTab = tabMode === 'period';
  const activeLine = isolatedPlayer ?? hoveredPlayer;
  const isClosed = isPeriodTab && selectedPeriod?.status === 'closed';
  const endMatchdayReached = isPeriodTab && selectedPeriod && maxMatchdayEver != null && maxMatchdayEver >= selectedPeriod.end_matchday;
  const champion = isClosed ? standings.find(s => s.rank === 1) : null;
  const coChampions = isClosed ? standings.filter(s => s.rank === 1) : [];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>{SSG_COMPETITION_NAME}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Small-sided-games standings — 0/1/3, same as the league you follow every week.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn(false)} onClick={onOpenHallOfFame}>
            <BookOpen size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Hall of Fame
          </button>
          <button style={btn(false)} onClick={() => setShowTvTokens(true)}>
            <Tv size={13} style={{ verticalAlign: -2, marginRight: 5 }} />TV tokens
          </button>
          <button style={btn(false)} onClick={onManageSessions}>
            <Settings2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Manage sessions
          </button>
        </div>
      </div>
      {showTvTokens && <SsgTvTokensAdmin onClose={() => setShowTvTokens(false)} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {periods.map(p => (
          <button key={p.id} style={btn(tabMode === 'period' && selectedPeriodId === p.id)}
            onClick={() => { setTabMode('period'); setSelectedPeriodId(p.id); }}>
            {p.name}{p.status === 'closed' ? ' 🏆' : ''}
          </button>
        ))}
        <button style={btn(tabMode === 'season')} onClick={() => setTabMode('season')}>Season</button>
        <button style={btn(tabMode === 'alltime')} onClick={() => setTabMode('alltime')}>All-time</button>
        {isPeriodTab && selectedPeriod && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
            MD {selectedPeriod.start_matchday}–{selectedPeriod.end_matchday}
            {daysRemaining != null ? ` · ${daysRemaining}d remaining` : ''}
          </span>
        )}
        {isPeriodTab && selectedPeriod && !isClosed && (
          <button
            style={{ ...btn(false, '#f59e0b'), marginLeft: 'auto', opacity: endMatchdayReached ? 1 : 0.6 }}
            onClick={() => setShowCloseConfirm(true)}
            title={endMatchdayReached ? undefined : "This period's matchday range doesn't look reached yet — you can still close it early if needed."}
          >
            <Award size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Close period
          </button>
        )}
      </div>

      {/* Champion banner — replaces the usual "who's leading" emphasis once a period is closed */}
      {isClosed && champion && (
        <div style={{
          ...card, display: 'flex', alignItems: 'center', gap: 16,
          border: '2px solid #f59e0b', background: 'linear-gradient(135deg, #f59e0b1a, var(--bg-card))',
        }}>
          <div style={{ fontSize: 40 }}>🏆</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Period Champion — {selectedPeriod.name}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }}>
              {coChampions.map(c => c.name).join(' & ')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
              {champion.total_points} pts{selectedPeriod.prize ? ` · 🎁 ${selectedPeriod.prize}` : ''}
            </div>
          </div>
        </div>
      )}

      {/* Standings table */}
      <div style={card} ref={leaderboardPanelRef}>
        <div style={sectionTitle}>
          <span>Standings</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn(false)} onClick={exportStandingsCsv} title="Export standings to CSV"><Download size={12} style={{ verticalAlign: -1, marginRight: 4 }} />CSV</button>
            <button style={btn(false)} onClick={exportLeaderboardPng} disabled={exportingPng} title="Export leaderboard panel to PNG">
              <ImageIcon size={12} style={{ verticalAlign: -1, marginRight: 4 }} />{exportingPng ? 'Exporting…' : 'PNG'}
            </button>
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '6px 8px' }}>Rank</th>
                  <th style={{ padding: '6px 8px' }}></th>
                  <th style={{ padding: '6px 8px' }}>Player</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>P</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>W</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>D</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>L</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Bonus</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Pts</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Avg</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>MVPs</th>
                  <th style={{ padding: '6px 8px' }}>Form</th>
                </tr>
              </thead>
              <tbody>
                {standings.map(s => (
                  <tr key={s.player_id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 8px' }}><RankCell rank={s.rank} sharedRank={s.shared_rank} /></td>
                    <td style={{ padding: '7px 8px' }}><DeltaArrow delta={s.rank_delta} /></td>
                    <td style={{ padding: '7px 8px', fontWeight: 600 }}>
                      {s.name}
                      {isPeriodTab && <BadgeChips badges={badgesByPlayer[s.player_id]} />}
                    </td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.played}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.won}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.drawn}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.lost}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.bonus_points}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700 }}>{s.total_points}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', color: 'var(--text-muted)' }}>{s.avg_points_per_session ?? '—'}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{s.mvp_count}</td>
                    <td style={{ padding: '7px 8px' }}><FormPills form={s.form} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bump chart */}
      {isPeriodTab && (
        <div style={card}>
          <div style={sectionTitle}>
            <span>Rank over sessions</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={btn(chartMode === 'rank')} onClick={() => setChartMode('rank')}>Rank</button>
              <button style={btn(chartMode === 'points')} onClick={() => setChartMode('points')}>Cumulative points</button>
              {isolatedPlayer != null && <button style={btn(false)} onClick={() => setIsolatedPlayer(null)}>Show all</button>}
            </div>
          </div>
          {chartData.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)' }}>No published sessions in this period yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                <YAxis
                  reversed={chartMode === 'rank'}
                  allowDecimals={false}
                  tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                  axisLine={false} tickLine={false}
                  label={chartMode === 'rank' ? undefined : undefined}
                />
                <RTooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                />
                {chartPlayers.map(p => {
                  const dim = activeLine != null && activeLine !== p.player_id;
                  return (
                    <Line
                      key={p.player_id}
                      type="monotone"
                      dataKey={`p${p.player_id}`}
                      name={p.name}
                      stroke={p.color}
                      strokeWidth={activeLine === p.player_id ? 3.5 : 2}
                      strokeOpacity={dim ? 0.15 : 1}
                      dot={{ r: 2.5, fillOpacity: dim ? 0.15 : 1 }}
                      activeDot={{ r: 4 }}
                      connectNulls
                      onMouseEnter={() => setHoveredPlayer(p.player_id)}
                      onMouseLeave={() => setHoveredPlayer(null)}
                      onClick={() => setIsolatedPlayer(prev => prev === p.player_id ? null : p.player_id)}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
          {/* Custom legend, top 5 only — the other 25+ muted-grey lines stay fully interactive
              directly on the chart (hover/click), they'd just make a full-squad legend unreadable. */}
          {chartData.length > 0 && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
              {chartPlayers.filter(p => p.top5).map(p => (
                <span
                  key={p.player_id}
                  onMouseEnter={() => setHoveredPlayer(p.player_id)}
                  onMouseLeave={() => setHoveredPlayer(null)}
                  onClick={() => setIsolatedPlayer(prev => prev === p.player_id ? null : p.player_id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer',
                    fontWeight: isolatedPlayer === p.player_id ? 700 : 400,
                    color: activeLine != null && activeLine !== p.player_id ? 'var(--text-dimmed)' : 'var(--text-secondary)',
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                  {p.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Session heatmap */}
      {isPeriodTab && (
        <div style={card}>
          <div style={sectionTitle}><span>Session heatmap</span></div>
          {gridLoading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
          ) : sessionsForGrid.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)' }}>No published sessions in this period yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 8px', textAlign: 'left', position: 'sticky', left: 0, background: 'var(--bg-card)' }}>Player</th>
                    {sessionsForGrid.map(s => (
                      <th key={s.id} style={{ padding: '4px 6px', fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(s.session_date)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {standings.map(player => (
                    <tr key={player.player_id}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--bg-card)' }}>{player.name}</td>
                      {sessionsForGrid.map(s => {
                        const pt = pointsGrid[s.id]?.[player.player_id];
                        const meta = pt ? ssgResultMeta(pt.base_points) : null;
                        const absent = !pt || pt.base_points == null;
                        const title = pt
                          ? `${meta?.label ?? 'Absent'} · base ${pt.base_points ?? '—'} · bonus ${pt.bonus_points || 0}${pt.bonus_reason ? ` (${pt.bonus_reason})` : ''}${pt.note ? ` · note: ${pt.note}` : ''}`
                          : 'Absent';
                        return (
                          <td key={s.id} title={title} style={{ padding: 2 }}>
                            <div style={{
                              width: 26, height: 22, borderRadius: 4, position: 'relative',
                              background: absent
                                ? 'repeating-linear-gradient(45deg, var(--border), var(--border) 3px, transparent 3px, transparent 6px)'
                                : (meta.color + '55'),
                              border: absent ? '1px dashed var(--border-strong)' : `1px solid ${meta.color}`,
                            }}>
                              {pt?.bonus_points > 0 && (
                                <span style={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bonus audit */}
      {isPeriodTab && (
        <div style={card}>
          <div style={sectionTitle}>
            <span>Bonus audit</span>
            <button style={btn(false)} onClick={exportBonusCsv}><Download size={12} style={{ verticalAlign: -1, marginRight: 4 }} />CSV</button>
          </div>
          {bonusRows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)' }}>No bonus points awarded in this period yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {[['date', 'Date'], ['name', 'Player'], ['amount', 'Amount'], ['reason', 'Reason']].map(([key, label]) => (
                    <th key={key} style={{ padding: '6px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleBonusSort(key)}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{label} <SortIcon active={bonusSort.key === key} dir={bonusSort.dir} /></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bonusRows.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 8px' }}>{fmtDate(r.session_date)}</td>
                    <td style={{ padding: '7px 8px', fontWeight: 600 }}>{r.name}</td>
                    <td style={{ padding: '7px 8px', fontWeight: 700 }}>+{r.amount}</td>
                    <td style={{ padding: '7px 8px', color: r.reason ? 'var(--text-secondary)' : '#ef4444', fontStyle: r.reason ? 'normal' : 'italic' }}>{r.reason || 'No reason given'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Close period confirmation */}
      {showCloseConfirm && selectedPeriod && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Close {selectedPeriod.name}?</h3>
              <button onClick={() => setShowCloseConfirm(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 0 }}>
                This freezes the final standings — <b>{coChampions.length > 1 ? 'these players tie for' : 'this player wins'} Period Champion</b> — and can't be undone from here.
              </p>
              {hasDraftSessions && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10, borderRadius: 8, background: '#ef44441a', color: '#ef4444', fontSize: 12.5, marginBottom: 14 }}>
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  This period still has a session in Draft — its points won't count until it's published. Publish it first, or close anyway to leave it out.
                </div>
              )}
              {!endMatchdayReached && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10, borderRadius: 8, background: '#f59e0b1a', color: '#f59e0b', fontSize: 12.5, marginBottom: 14 }}>
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  Matchday {selectedPeriod.end_matchday} doesn't look reached yet, going by sessions created so far.
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Final standings (top 5)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                {standings.slice(0, 5).map(s => (
                  <div key={s.player_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 8px', borderRadius: 6, background: s.rank === 1 ? '#f59e0b22' : 'var(--bg-app)' }}>
                    <span style={{ width: 24, fontWeight: 700 }}>{s.shared_rank ? '=' : ''}{s.rank}</span>
                    <span style={{ flex: 1 }}>{s.name}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{s.total_points} pts</span>
                    {s.rank === 1 && <span>🏆</span>}
                  </div>
                ))}
              </div>
              {closeError && <div style={{ fontSize: 12.5, color: '#ef4444', marginBottom: 10 }}>{closeError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button style={btn(false)} onClick={() => setShowCloseConfirm(false)}>Cancel</button>
                <button style={btn(true, '#f59e0b')} onClick={handleClosePeriod} disabled={closing}>
                  <Award size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                  {closing ? 'Closing…' : 'Confirm & close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
